/**
 * The payslip BCEA section 33 requires, and the page it prints on.
 *
 * s33(1) is not a suggestion and not a template — it is a list, and an employer
 * must hand over every item on it in writing on each day the employee is paid:
 *
 *   (a) The employer's name and address;
 *   (b) the employee's name and occupation;
 *   (c) the period for which the payment is made;
 *   (d) the employee's remuneration in money;
 *   (e) the amount and purpose of any deduction made from the remuneration;
 *   (f) the actual amount paid to the employee; and
 *   (g) if relevant to the calculation of that employee's remuneration—
 *       (i)   the employee's rate of remuneration and overtime rate;
 *       (ii)  number of ordinary and overtime hours worked ... during the period;
 *       (iii) the number of hours worked ... on a Sunday or public holiday
 *             during that period; ...
 *
 * There is no exemption for a household, a domestic worker or a casual. The
 * site has been promising this since the day it went up — "from clock-in to
 * payslip" — and it did not exist.
 *
 * Split in two on purpose: `buildPayslip` decides what the numbers are and
 * `payslipHtml` decides what they look like. The first is where a dispute is
 * answered and is worth testing line by line; the second is a page.
 *
 * SOUTH AFRICA ONLY. See the future-markets note.
 */

import { applyAdjustments } from "./adjustments";
import { longISODate } from "./certificate";
import type { UnworkedHoliday } from "./entitlements";
import { hoursLabel, money } from "./time";
import { payForDay, splitHoursOn, type DayContext } from "./shifts";
import type { EmploymentTerms, PayAdjustment, Shift } from "./types";

export interface PayslipLine {
  label: string;
  /** The working, in words. "12 September · Heritage Day · paid at double". */
  detail?: string;
  hours?: number;
  amount: number;
}

export interface Payslip {
  employer: { name: string; address: string };      // s33(1)(a)
  employee: { name: string; occupation: string };   // s33(1)(b)
  period: { from: string; to: string };             // s33(1)(c)
  currency: string;
  /** s33(1)(g)(i). Hourly, as the terms state them. */
  rates: { ordinary: number; overtime: number; sunday: number };
  /** s33(1)(g)(ii) and (iii). */
  hours: { ordinary: number; overtime: number; sunday: number; holiday: number; total: number };
  earnings: PayslipLine[];                          // s33(1)(d)
  additions: PayslipLine[];
  deductions: PayslipLine[];                        // s33(1)(e)
  gross: number;
  net: number;                                      // s33(1)(f)
  issuedOn: string;
  /** Said plainly rather than implied. */
  notes: string[];
}

export interface PayslipInput {
  employer: { name: string; address: string };
  employee: { name: string; occupation: string };
  period: { from: string; to: string };
  /** Closed shifts inside the period. An open one has not been earned yet. */
  shifts: Shift[];
  termsFor: (shift: Shift) => EmploymentTerms;
  dayFor: (shift: Shift) => DayContext;
  /** Public holidays paid but not worked — s18(2)(a). */
  unworked: UnworkedHoliday[];
  adjustments: PayAdjustment[];
  /** The terms in force at the end of the period, for the rates on the page. */
  terms: EmploymentTerms;
  issuedOn: string;
}

const dayOfMonth = (iso: string) => Number(iso.slice(8, 10));

export function buildPayslip(input: PayslipInput): Payslip {
  const hours = { ordinary: 0, overtime: 0, sunday: 0, holiday: 0, total: 0 };
  const earnings: PayslipLine[] = [];

  // Grouped by what the day was paid as, because that is the question a worker
  // asks — not "what did Tuesday come to" but "why is Heritage Day different".
  const buckets = new Map<string, { hours: number; amount: number; days: string[] }>();

  for (const shift of input.shifts) {
    if (!shift.clockOut) continue;
    const terms = input.termsFor(shift);
    const day = input.dayFor(shift);
    const split = splitHoursOn(shift, terms, day);
    const pay = payForDay(shift, terms, day);

    hours.ordinary += split.ordinary;
    hours.overtime += split.overtime;
    hours.sunday += split.sunday;
    hours.holiday += split.holiday;
    hours.total += split.total;

    // An ordinary day is the one basis with two rates in it, and s33(1)(g)(ii)
    // asks for ordinary and overtime hours separately. Lumping them gave a row
    // labelled "Ordinary hours" whose hours were neither the ordinary ones nor
    // the total, and whose amount silently carried the 1.5. For every other
    // basis the Act prices the whole day as one thing, so there is nothing to
    // split — a public holiday has no overtime bucket at all.
    if (pay.basis === "ordinary") {
      put("ordinary", split.ordinary, split.ordinary * terms.rate, day);
      if (split.overtime > 0) {
        put("overtime", split.overtime, split.overtime * terms.rate * terms.ot, day);
      }
    } else {
      put(pay.basis, split.total, pay.amount, day);
    }
  }

  function put(key: string, hrs: number, amount: number, day: DayContext) {
    const b = buckets.get(key) ?? { hours: 0, amount: 0, days: [] };
    b.hours += hrs;
    b.amount += amount;
    if (day.holiday) b.days.push(day.holiday.name);
    buckets.set(key, b);
  }

  const LABEL: Record<string, string> = {
    ordinary: "Ordinary hours",
    overtime: "Overtime",
    sunday: "Sunday",
    "sunday-floor": "Sunday",
    "holiday-double": "Public holiday worked",
    "holiday-plus-earned": "Public holiday worked",
  };
  const WHY: Record<string, string> = {
    ordinary: "",
    overtime: "beyond the ordinary day",
    sunday: "at the Sunday rate",
    "sunday-floor": "a short Sunday still pays a full day — BCEA s16(2)",
    "holiday-double": "at least double the day's wage — BCEA s18(2)(b)(i)",
    "holiday-plus-earned": "a day's wage plus the hours worked — BCEA s18(2)(b)(ii)",
  };

  // A fixed order, not the order the shifts happened to arrive in. Insertion
  // order put Overtime between the two public-holiday lines on a month where
  // the holiday fell before the long day, which reads as though somebody had
  // shuffled the page. This is the order s33(1)(g) itself lists them in, and
  // the order of the hour totals above.
  const ORDER = [
    "ordinary", "overtime", "sunday", "sunday-floor",
    "holiday-double", "holiday-plus-earned",
  ];
  const ordered = [...buckets.entries()].sort(
    (a, b) => ORDER.indexOf(a[0]) - ORDER.indexOf(b[0]),
  );

  for (const [basis, b] of ordered) {
    const names = [...new Set(b.days)];
    earnings.push({
      label: LABEL[basis] ?? basis,
      detail: [names.join(", "), WHY[basis]].filter(Boolean).join(" · ") || undefined,
      hours: b.hours,
      amount: b.amount,
    });
  }

  // s18(2)(a). Not a shift, not overtime — a day the Act pays for.
  for (const u of input.unworked) {
    earnings.push({
      label: "Public holiday not worked",
      detail: `${dayOfMonth(u.holiday.date)} ${u.holiday.name} · paid, not worked — BCEA s18(2)(a)`,
      amount: u.amount,
    });
  }

  const gross = earnings.reduce((sum, l) => sum + l.amount, 0);
  const totals = applyAdjustments(gross, input.adjustments);

  const toLine = (a: PayAdjustment): PayslipLine => ({
    // s33(1)(e) wants the purpose, not a category.
    label: a.purpose,
    detail: `${dayOfMonth(a.onDate)} ${longISODate(a.onDate).split(" ").slice(1).join(" ")}`,
    amount: a.amount,
  });

  const notes: string[] = [];
  // Said rather than implied: the Act asks for "the actual amount paid", and
  // Shifty does not know whether anybody paid it. Claiming otherwise on a
  // document somebody may take to the CCMA would be a lie of layout.
  notes.push(
    "Shifty records hours and what they come to. It does not record the payment itself, " +
    "so the net figure is what is owed for the period rather than proof of a transfer.",
  );

  return {
    employer: input.employer,
    employee: input.employee,
    period: input.period,
    currency: input.terms.currency,
    rates: {
      ordinary: input.terms.rate,
      overtime: input.terms.rate * input.terms.ot,
      sunday: input.terms.rate * input.terms.sunday,
    },
    hours,
    earnings,
    additions: input.adjustments.filter((a) => a.direction === "addition").map(toLine),
    deductions: input.adjustments.filter((a) => a.direction === "deduction").map(toLine),
    gross,
    net: totals.net,
    issuedOn: input.issuedOn,
    notes,
  };
}

/* ---------- the page ---------- */

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

export function payslipHtml(p: Payslip): string {
  const m = (n: number) => esc(money(n, p.currency));

  const lines = (title: string, rows: PayslipLine[]) =>
    rows.length === 0 ? "" : `
      <tr><th colspan="3" class="grp">${esc(title)}</th></tr>
      ${rows.map((l) => `
        <tr>
          <td>${esc(l.label)}${l.detail ? `<div class="sub">${esc(l.detail)}</div>` : ""}</td>
          <td class="num">${l.hours != null ? hoursLabel(l.hours) : ""}</td>
          <td class="num">${m(l.amount)}</td>
        </tr>`).join("")}`;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>Payslip — ${esc(p.employee.name)}</title>
<style>
  body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #171726;
         margin: 0; padding: 32px; font-size: 12px; line-height: 1.5; }
  h1 { font-size: 20px; margin: 0 0 2px; letter-spacing: -0.4px; }
  .muted { color: #696A79; }
  .head { display: flex; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
  .box { border: 1px solid #E9E7F0; border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; }
  td, th { padding: 7px 0; text-align: left; vertical-align: top; }
  th.grp { padding-top: 14px; font-size: 10px; letter-spacing: .09em;
           text-transform: uppercase; color: #696A79; border-bottom: 1px solid #E9E7F0; }
  .num { text-align: right; white-space: nowrap; }
  .sub { color: #696A79; font-size: 10.5px; }
  .tot td { border-top: 2px solid #171726; font-weight: 700; padding-top: 10px; }
  .grid { display: flex; gap: 28px; flex-wrap: wrap; }
  .grid div { font-size: 11px; }
  .note { color: #696A79; font-size: 10.5px; margin-top: 18px; }
</style>
</head><body>

<div class="head">
  <div>
    <h1>Payslip</h1>
    <div class="muted">${esc(longISODate(p.period.from))} to ${esc(longISODate(p.period.to))}</div>
  </div>
  <div style="text-align:right">
    <div><strong>${esc(p.employer.name)}</strong></div>
    <div class="muted">${esc(p.employer.address)}</div>
  </div>
</div>

<div class="box">
  <div><strong>${esc(p.employee.name)}</strong> · ${esc(p.employee.occupation)}</div>
  <div class="grid" style="margin-top:8px">
    <div class="muted">Ordinary rate<br><strong>${m(p.rates.ordinary)} an hour</strong></div>
    <div class="muted">Overtime rate<br><strong>${m(p.rates.overtime)} an hour</strong></div>
    <div class="muted">Sunday rate<br><strong>${m(p.rates.sunday)} an hour</strong></div>
  </div>
  <div class="grid" style="margin-top:12px">
    <div class="muted">Ordinary hours<br><strong>${hoursLabel(p.hours.ordinary)}</strong></div>
    <div class="muted">Overtime hours<br><strong>${hoursLabel(p.hours.overtime)}</strong></div>
    <div class="muted">Sunday hours<br><strong>${hoursLabel(p.hours.sunday)}</strong></div>
    <div class="muted">Public holiday hours<br><strong>${hoursLabel(p.hours.holiday)}</strong></div>
  </div>
</div>

<table>
  ${lines("Earnings", p.earnings)}
  <tr class="tot"><td>Remuneration in money</td><td></td><td class="num">${m(p.gross)}</td></tr>
  ${lines("Added", p.additions)}
  ${lines("Deductions", p.deductions)}
  <tr class="tot"><td>Net pay</td><td></td><td class="num">${m(p.net)}</td></tr>
</table>

${p.notes.map((n) => `<p class="note">${esc(n)}</p>`).join("")}
<p class="note">Issued ${esc(longISODate(p.issuedOn))} · Prepared by Shifty from the attendance register.</p>

</body></html>`;
}
